import { Link, useNavigate } from "react-router";
import { HOLD_BACKGROUND, HoldBadge } from "../components/HoldBadge";
import type { AppointmentStatus, AppointmentView } from "@vaulted/shared";
import { BOARD_STATUSES, STATUS_LABELS, isOpenStatus } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, post } from "../api";
import { GfeBadge } from "../components/Gfe";
import { FormsBadge, NEXT_ACTION, time12 } from "../components/AppointmentDialog";
import { BookingDialog, type BookingPrefill } from "../components/BookingDialog";
import { Alert, Button, Loading, PageHeader, cx } from "../components/ui";
import { DateBar, addDays, dayLabel, nowMinutesIn, todayIn, useCalendar } from "./Calendar";
import { displayName } from "./Clients";
import { useWaitingRequests, whenRequested } from "./MobileRequests";
import { useSession } from "../session";

const hint: Partial<Record<AppointmentStatus, string>> = {
  unconfirmed: "Booked, not yet confirmed",
  confirmed: "Expected today",
  arrived: "Here, waiting to start",
  active: "In the chair",
  completed: "Done",
};

/**
 * A day's visits with a one-tap next step: mobile requests waiting on top, then the clinic's board
 * by status, then mobile visits, then each event. Opens on today; arrows and the date box show other days.
 */
export function FrontDeskPage() {
  const { can } = useSession();
  const booking = can("appointments.manage");
  const requests = useWaitingRequests();
  // The clinic's zone comes back with the first load; until then the browser's date is close enough.
  const [tzDate, setTzDate] = useState<string | null>(null);
  const today = tzDate ?? todayIn();
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? today;
  const isToday = day === today;
  // Every 15 seconds: check-ins and forms done on the iPad show up quickly.
  const cal = useCalendar(day, day, booking, 15_000);
  const tz = cal.data?.timezone;
  if (tz && todayIn(tz) !== today) setTzDate(todayIn(tz));
  const go = (d: string) => setPicked(d === today ? null : d);
  const qc = useQueryClient();
  const openVisit = useNavigate();
  const setOpenId = (id: string) => openVisit(`/visits/${id}`);
  const [walkIn, setWalkIn] = useState<BookingPrefill | null>(null);
  const staffName = (id: string | null) => cal.data?.staff.find((s) => s.id === id)?.name ?? "";

  const step = useMutation({
    mutationFn: (v: { id: string; to: AppointmentStatus }) => post(`/api/appointments/${v.id}/status`, { status: v.to }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
  });

  // Mobile requests nobody has answered yet sit in the strip on top, not on the board.
  const appts = (cal.data?.appointments ?? [])
    .filter((a) => a.start.slice(0, 10) === day && (a.context !== "mobile" || a.bookingStatus === "confirmed"))
    .sort((x, y) => x.start.localeCompare(y.start));
  const ended = appts.filter((a) => !isOpenStatus(a.status));
  const live = appts.filter((a) => isOpenStatus(a.status));
  // Finished event visits join the clinic's Completed column (the event's section is for who's still to come).
  const clinic = live.filter((a) => a.context === "clinic" || (a.context === "event" && a.status === "completed"));
  const mobile = live.filter((a) => a.context === "mobile");
  const events = [...new Set(live.filter((a) => a.context === "event" && a.status !== "completed").map((a) => a.eventName ?? "Event"))].map((name) => {
    const all = live.filter((a) => a.context === "event" && (a.eventName ?? "Event") === name);
    const shift = cal.data?.eventShifts.find((s) => s.eventName === name && s.start.slice(0, 10) === day);
    return { name, list: all.filter((a) => a.status !== "completed"), all, shift };
  });
  const elsewhere = mobile.length > 0 || events.length > 0;
  const card = (a: AppointmentView) => (
    <Card
      key={a.id}
      a={a}
      staffName={staffName}
      busy={step.isPending && step.variables?.id === a.id}
      onOpen={() => setOpenId(a.id)}
      // Starting without signed forms asks for a reason in the appointment window.
      onStep={(to) => (to === "active" && a.formsNeeded.length ? setOpenId(a.id) : step.mutate({ id: a.id, to }))}
    />
  );
  const startWalkIn = () => {
    const m = Math.ceil(nowMinutesIn(tz) / 5) * 5;
    setWalkIn({ date: today, time: `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`, walkIn: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Front desk" />
      <DateBar date={day} label={`${dayLabel(day)}${isToday ? " (today)" : ""}`} onPrev={() => go(addDays(day, -1))} onToday={() => go(today)} onNext={() => go(addDays(day, 1))} onPick={go}>
        <Button onClick={() => setWalkIn({ date: day })}>Book</Button>
        {isToday && (
          <Button variant="primary" onClick={startWalkIn}>
            Walk-in
          </Button>
        )}
      </DateBar>
      {(requests.data?.length ?? 0) > 0 && (
        <section className="flex flex-col gap-2 rounded-[var(--radius-panel)] border-2 border-wait/50 bg-wait-wash/40 p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-semibold">Mobile requests waiting ({requests.data!.length})</h2>
            <Link to="/mobile-requests/tab/recent" className="text-sm font-semibold text-brand">
              Recent requests
            </Link>
          </div>
          <ul className="flex flex-col gap-1.5">
            {[...requests.data!]
              .sort((a, b) => a.start.localeCompare(b.start))
              .map((r) => (
                <li key={r.bookingId}>
                  <Link to={`/mobile-requests/${r.bookingId}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[var(--radius-control)] bg-white px-3 py-2 hover:ring-1 hover:ring-brand">
                    <span className="font-semibold">{whenRequested(r)}</span>
                    <span className="text-sm text-ink-soft">
                      {r.guests.map((g) => g.name).join(", ")} · {r.address}
                    </span>
                    <span className="ml-auto text-sm text-ink-soft">{r.candidates.length ? `Free: ${r.candidates.map((c) => c.name).join(", ")}` : "No nurse is free then"}</span>
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      )}
      {step.error && <Alert>{errorMessage(step.error)}</Alert>}
      {cal.error && <Alert>{errorMessage(cal.error)}</Alert>}
      {!booking ? null : !cal.data ? (
        !cal.error && <Loading />
      ) : (
        <>
          {elsewhere && <h2 className="font-display text-xl">In clinic</h2>}
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
            {BOARD_STATUSES.map((s) => {
              const list = clinic.filter((a) => a.status === s);
              return (
                <section key={s} className="flex flex-col rounded-[var(--radius-panel)] border border-line bg-mist/50">
                  <header className="border-b border-line px-3 pt-2.5 pb-1.5">
                    <h3 className="text-sm font-semibold">
                      {STATUS_LABELS[s]} <span className="text-muted">({list.length})</span>
                    </h3>
                    <p className="text-xs text-muted">{hint[s]}</p>
                  </header>
                  <ul className="flex flex-col gap-2 p-2">
                    {list.map(card)}
                    {!list.length && <li className="px-1 py-3 text-center text-xs text-muted">None</li>}
                  </ul>
                </section>
              );
            })}
          </div>
          {mobile.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="font-display text-xl">
                Mobile <span className="font-sans text-base text-muted">({mobile.length})</span>
              </h2>
              <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">{mobile.map(card)}</ul>
            </section>
          )}
          {events.map((e) => (
            <section key={e.name} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="font-display text-xl">{e.name}</h2>
                {e.shift && (
                  <span className="text-sm text-ink-soft">
                    {time12(e.shift.start)}–{time12(e.shift.end)}
                  </span>
                )}
                <span className="text-sm text-ink-soft">
                  {e.all.filter((a) => a.status === "arrived" || a.status === "active" || a.status === "completed").length} of {e.all.length} checked in
                </span>
                {e.shift && (
                  <Link to={`/events/check-in/${e.shift.occurrenceId}`} className="text-sm font-bold text-brand">
                    Check-in screen →
                  </Link>
                )}
              </div>
              <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">{e.list.map(card)}</ul>
            </section>
          ))}
          {ended.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-semibold text-muted">Cancelled and no-shows ({ended.length})</summary>
              <ul className="mt-2 flex flex-col gap-1">
                {ended.map((a) => (
                  <li key={a.id}>
                    <button onClick={() => setOpenId(a.id)} className="hover:underline">
                      {time12(a.start)} {displayName(a.client)}, {a.serviceName}: {STATUS_LABELS[a.status]}
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      {walkIn && cal.data && <BookingDialog prefill={walkIn} staff={cal.data.staff} onClose={() => setWalkIn(null)} onBooked={() => setWalkIn(null)} />}
    </div>
  );
}

function Card({ a, staffName, busy, onOpen, onStep }: { a: AppointmentView; staffName: (id: string | null) => string; busy: boolean; onOpen: () => void; onStep: (to: AppointmentStatus) => void }) {
  const next = NEXT_ACTION[a.status];
  const nav = useNavigate();
  return (
    <li className="rounded-[var(--radius-control)] border-l-4 bg-white p-2.5 shadow-sm" style={a.holdUntil ? { borderColor: "#d97706", background: HOLD_BACKGROUND } : { borderColor: a.color ?? "var(--color-brand)" }}>
      {/* The name opens their file; anywhere else on the card opens the visit. */}
      <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen())} className="block w-full cursor-pointer text-left">
        <div className="flex items-center gap-1 text-sm font-semibold">
          <span className="tabular-nums">{time12(a.start)}</span>
          <Link to={`/clients/${a.client.id}`} onClick={(e) => e.stopPropagation()} className="truncate hover:underline">
            {displayName(a.client)}
          </Link>
        </div>
        <div className="truncate text-xs text-ink-soft">
          {a.serviceName}
          {a.addOns.length > 0 && ` + ${a.addOns.length} add-on${a.addOns.length > 1 ? "s" : ""}`}
        </div>
        <div className="truncate text-xs text-muted">{a.staffId ? staffName(a.staffId) : "No nurse"}</div>
        {a.context === "mobile" && a.address && <div className="truncate text-xs text-muted">{a.address}</div>}
        {a.holdUntil && (
          <div className="mt-1">
            <HoldBadge until={a.holdUntil} />
          </div>
        )}
        <div className="mt-0.5 flex flex-wrap gap-x-2">
          <FormsBadge a={a} />
          {a.status !== "cancelled" && a.status !== "completed" && <GfeBadge g={a.gfe} compact />}
          {a.charts.some((c) => c.status === "signed" && c.templateKey !== "adverse") && <span className="text-xs font-semibold text-ok">Charted ✓</span>}
        </div>
        {a.client.schedulingAlert && <div className="mt-1 truncate text-xs font-semibold text-wait">⚠ {a.client.schedulingAlert}</div>}
        {a.guardianCheck ? (
          <div className="mt-1 text-xs font-semibold text-wait">Check guardian: {a.guardianCheck.name}</div>
        ) : (
          a.client.minorWithoutGuardian && <div className="mt-1 text-xs font-semibold text-wait">Minor: no guardian linked</div>
        )}
      </div>
      {next && !a.holdUntil && (
        <Button variant={a.status === "confirmed" ? "primary" : undefined} className={cx("mt-2 w-full")} busy={busy} onClick={() => (next.visit ? nav(`/visits/${a.id}`) : onStep(next.to))}>
          {next.label}
        </Button>
      )}
    </li>
  );
}
